import { uiText } from '@/i18n/interface';

import React, { useEffect } from 'react';
import { Progress } from "@/components/ui/progress";
import { useSecureAuth } from '../hooks/useSecureAuth';

interface PasswordStrengthIndicatorProps {
  password: string;
  onStrengthChange: (isStrong: boolean, errors: string[]) => void;
}

interface PasswordValidationResult {
  valid: boolean;
  errors: string[];
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  onStrengthChange
}) => {
  const { validatePasswordStrength } = useSecureAuth();

  const validatePassword = async (password: string) => {
    if (!password) {
      onStrengthChange(false, []);
      return { strength: 0, errors: [], isStrong: false };
    }

    try {
      // Use the secure server-side validation
      const result = await validatePasswordStrength(password);

      // Properly handle the result from Supabase
      const validationResult: PasswordValidationResult = {
        valid: (result as any)?.valid || false,
        errors: (result as any)?.errors || ['Failed to validate password']
      };

      const isStrong = validationResult.valid;
      const errors = validationResult.errors;

      // Calculate strength based on validation results
      const strength = isStrong ? 100 : Math.max(20, 100 - (errors.length * 20));

      onStrengthChange(isStrong, errors);
      return { strength, errors, isStrong };
    } catch (error) {
      console.error('Password validation error:', error);
      const errors = ['Failed to validate password'];
      onStrengthChange(false, errors);
      return { strength: 20, errors, isStrong: false };
    }
  };

  const [validationState, setValidationState] = React.useState({
    strength: 0,
    errors: [] as string[],
    isStrong: false
  });

  useEffect(() => {
    const validateAsync = async () => {
      const result = await validatePassword(password);
      setValidationState(result);
    };

    validateAsync();
  }, [password]);

  const getStrengthText = () => {
    if (validationState.strength < 40) return uiText("Weak");
    if (validationState.strength < 80) return uiText("Medium");
    return uiText("Strong");
  };

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <Progress value={validationState.strength} className="flex-1" />
        <span className={`text-sm font-medium ${
          validationState.isStrong ? 'text-green-600' : validationState.strength < 40 ? 'text-red-600' : 'text-yellow-600'
        }`}>
          {getStrengthText()}
        </span>
      </div>

      {validationState.errors.length > 0 && (
        <div className="space-y-1">
          {validationState.errors.map((error, index) => (
            <p key={index} className="text-xs text-red-600">
              • {error}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
